import type {FabricObject} from '@postermywall/fabricjs-2';

export type SnapAxis = 'x' | 'y';

export interface SnapPoint {
  x: number;
  y: number;
}

/** An axis-aligned box in the scene (poster) plane. */
export interface SnapBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface CanvasBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * A straight line the target can snap to that is not another item's point: a canvas edge, a canvas centre
 * line, a bleed line. `x` guides are vertical lines at x = value, `y` guides horizontal lines at y = value.
 */
export interface SnapGuide {
  axis: SnapAxis;
  value: number;
  /** Extent the line is drawn over, along the other axis. */
  from: number;
  to: number;
}

export enum SnapLineStyle {
  /** Dashed segment between the target's point and the matched item point. */
  OBJECT = 'object',
  /** Solid line over the guide's whole extent. */
  GUIDE = 'guide',
}

export interface SnapLine {
  axis: SnapAxis;
  value: number;
  from: number;
  to: number;
  style: SnapLineStyle;
}

/** A gap drawn as a line with end ticks and a pill label at its middle. */
export interface Measurement {
  axis: SnapAxis;
  from: number;
  to: number;
  /** Position on the other axis the line is drawn at. */
  at: number;
  label: string;
}

export interface MoveSnapInput {
  /** Reference points on the target: its corners and centre. */
  origins: SnapPoint[];
  /** Other items' corners and centres. */
  candidates: SnapPoint[];
  guides: SnapGuide[];
  /** Snap distance in scene units. */
  margin: number;
}

export interface MoveSnapResult {
  deltaX: number;
  deltaY: number;
  lines: SnapLine[];
}

/** Turns a scene-pixel length into the text shown in a pill, in whatever unit the poster is measured in. */
export type LengthFormatter = (pixels: number) => string;

export interface EqualSpacingInput {
  target: SnapBox;
  neighbours: SnapBox[];
  axis: SnapAxis;
  margin: number;
  /** A line snap already decided this axis: gaps that are already equal are labelled, the target is not moved. */
  locked: boolean;
  formatLength: LengthFormatter;
}

export interface EqualSpacingResult {
  delta: number;
  measurements: Measurement[];
}

export type ResizeCornerHandle = (typeof RESIZE_CORNER_HANDLES)[number];
export type ResizeSideHandle = (typeof RESIZE_SIDE_HANDLES)[number];
export type ResizeHandle = ResizeCornerHandle | ResizeSideHandle;

/** How much the last resize snap changed the object's scale; removed before the next decision. */
export interface ResizeDrift {
  scaleX: number;
  scaleY: number;
}

export interface ResizeSnapInput {
  target: FabricObject;
  handle: ResizeHandle;
  /** Both axes scale by one factor, so only the closer match can be honoured. Never set for a side handle. */
  isUniform: boolean;
  candidates: SnapPoint[];
  guides: SnapGuide[];
  margin: number;
  drift: ResizeDrift;
}

export interface ResizeSnapResult {
  lines: SnapLine[];
  drift: ResizeDrift;
}

/** One pointer step of a resize, after the handle has applied the pointer's movement to `target`. */
export interface ResizeSnapStep {
  target: FabricObject;
  handle: ResizeHandle;
  /** Both axes scale by one factor, so only the closer match can be honoured. Never set for a side handle. */
  isUniform: boolean;
  pointerEvent: PointerEvent;
}

/** The key that, held down, shows the distance readout; see `isDistanceReadoutModifierHeld`. */
export const DISTANCE_READOUT_KEY_NAME = 'Alt';

/** Everything the overlay draws for one pointer event. */
export interface GuideFrame {
  lines: SnapLine[];
  measurements: Measurement[];
}

export const EMPTY_GUIDE_FRAME: GuideFrame = {lines: [], measurements: []};

/** Snap distance in screen pixels, so the reach feels the same at every zoom level. */
export const SNAP_MARGIN_SCREEN_PX = 4;
/** Gaps equal to within this many scene units count as already equal when the axis is locked by a line snap. */
export const EQUAL_GAP_TOLERANCE = 0.5;
/** How far (scene units) a snapped reference point may sit from the value it snapped to and still get a line. */
export const SNAP_LINE_TOLERANCE = 0.01;
/** The editor's corner resize handles (see `CustomCornerControl`); both scene axes may snap. */
export const RESIZE_CORNER_HANDLES = ['tl', 'tr', 'bl', 'br'] as const;
/** The editor's side resize handles (see `CustomSideControl`); only the axis across the handle's edge may snap. The rotation handle is not snapped. */
export const RESIZE_SIDE_HANDLES = ['ml', 'mr', 'mt', 'mb'] as const;
export const NO_RESIZE_DRIFT: ResizeDrift = {scaleX: 0, scaleY: 0};

export const OVERLAY_LINE_WIDTH_PX = 1;
export const OVERLAY_OBJECT_LINE_DASH = '5 3';
export const OVERLAY_MEASUREMENT_TICK_PX = 4;
export const OVERLAY_LABEL_HEIGHT_PX = 18;
/** Line kept visible on each side of a pill that sits on its line, and between the line and a pill moved off it. */
export const OVERLAY_LABEL_LINE_CLEARANCE_PX = 6;
export const OVERLAY_LABEL_PADDING_X_PX = 6;
export const OVERLAY_LABEL_FONT_SIZE_PX = 11;
export const OVERLAY_LABEL_TEXT_COLOR = '#ffffff';
export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
